import { ElementType, ReactNode } from "react";

import LazyImage from "../Image/LazyImage";
import Typography from "../Typography/Typography";
import Card from "../Card/Card";
import Box from "../Box/Box";
import VrmBox from "../VrmBox/VrmBox";
import LotNumberBox from "../LotNumberBox/LotNumberBox";
import Link from "../Link/Link";

export interface VehicleCardProps {
  /**
  Allows the user to specify the horizontal margin of the card. Accepts values from the space property in the theme(numbers 0-9 that, multiplied by 4, represent the number of pixels), strings corresponding to the number of pixels or arrays(string[] | number[]) for responsive values.
  (eg. marginX: 1 -> margin-left: 4px, margin-right: 4px;
      marginX: 2 -> margin-left: 8px, margin-right: 8px;
      marginX: "1px" -> margin-left: 1px, margin-right: 1px;
      marginX: "2px" -> margin-left: 2px, margin-right: 2px;
      marginX: [1,2] -> margin-left: 4px, margin-right: 4px up to the first breakpoint and margin-left: 8px, margin-right: 8px to the second;)
  */
  marginX?: number | string | (string | number)[];
  /**
  Allows the user to specify the vertical margin of the card. Accepts values from the space property in the theme(numbers 0-9 that, multiplied by 4, represent the number of pixels), strings corresponding to the number of pixels or arrays(string[] | number[]) for responsive values.
  (eg. marginY: 1 -> margin-top: 4px, margin-bottom: 4px;
      marginY: 2 -> margin-top: 8px, margin-bottom: 8px;
      marginY: "1px" -> margin-top: 1px, margin-bottom: 1px;
      marginY: "2px" -> margin-top: 2px, margin-bottom: 2px;
      marginY: [1,2] -> margin-left: 4px, margin-right: 4px up to the first breakpoint and margin-left: 8px, margin-right: 8px to the second;)
  */
  marginY?: number | string | (string | number)[];
  /**
  Allows the user to specify any custom styles.
  */
  css?: string;
  primaryDescription: string;
  /**
  Allows the user to specify a link for the primary description.
  */
  primaryDescriptionLink?: string;
  secondaryDescription?: string;
  /**
   image: { url: string; alt: string };
   */
  image: { url: string; alt: string };
  vrm: string;
  lotNumber?: string;
  children?: ReactNode;
  htmlTag?: ElementType;
}

export default function VehicleCard({
  children,
  marginX = 6,
  marginY = 6,
  primaryDescription,
  primaryDescriptionLink,
  secondaryDescription,
  image,
  vrm,
  lotNumber,
  htmlTag,
  ...props
}: VehicleCardProps) {
  return (
    <Card
      marginX={marginX}
      marginY={marginY}
      paddingX={0}
      paddingY={0}
      width="300px"
      height="auto"
      htmlTag={htmlTag}
      aria-label="vehicle card"
      {...props}
    >
      <Box display="flex" flexDirection="column-reverse">
        {!primaryDescriptionLink && (
          <Typography variant="h5" margin="16px 16px 14px 16px">
            {primaryDescription}
          </Typography>
        )}
        {primaryDescriptionLink && (
          <Typography
            variant="h5"
            color="primary-light-blue"
            margin="16px 16px 14px 16px"
          >
            <Link
              href={primaryDescriptionLink}
              target="_blank"
              underline="always"
            >
              {primaryDescription}
            </Link>
          </Typography>
        )}
        <Box>
          {lotNumber && (
            <LotNumberBox
              lotNumber={lotNumber}
              position="absolute"
              top="115px"
              left="16px"
              paddingX={2}
            ></LotNumberBox>
          )}
          <VrmBox
            vrm={vrm}
            position="absolute"
            top="147px"
            left="16px"
            paddingX={2}
          ></VrmBox>
          <LazyImage
            width="100%"
            height="186px"
            src={image.url}
            alt={image.alt}
          ></LazyImage>
        </Box>
      </Box>
      {secondaryDescription && (
        <Typography variant="body" margin="0 16px 16px 16px">
          {secondaryDescription}
        </Typography>
      )}
      <Box
        style={{
          marginTop: "auto",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {children}
      </Box>
    </Card>
  );
}
